import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Attachment } from '@ee/common/models';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PdfViewerComponent } from 'ng2-pdf-viewer';
import { AnnotationMode } from 'pdfjs-dist';
import { DocumentService } from '@ee/common/services';
import { DOCX_FILE_MIME_TYPES, PDF_FILE_EXTENSION } from '@ee/common/constants';

/**
 * Self-hosted pdf.js runtime assets, copied out of `pdfjs-dist` by the `angular.json` asset globs.
 * Left to itself ng2-pdf-viewer fetches the worker from cdn.jsdelivr.net and the cMaps and
 * annotation images from unpkg.com at runtime, putting a third-party CDN in the critical path of
 * every document preview. Resolved against `baseURI` so a non-root base href still works.
 */
const PDFJS_ASSETS_URL = new URL('assets/pdfjs/', document.baseURI).toString();

@Component({
  selector: 'ee-pdf-preview-dialog',
  template: `
    @if (!hideTitle) {
      <h2 mat-dialog-title>{{ title ?? 'Preview' }}</h2>
    }
    <div mat-dialog-content class="pdf-wrapper">
      @if (!pdfSrc) {
        <div class="flex flex-row flex-1 justify-center items-center">
          <ee-loading message="Loading..."></ee-loading>
        </div>
      } @else {
        <pdf-viewer class="viewer" [src]="pdfSrc" [show-all]="true" [autoresize]="true" [original-size]="false"
                    [show-borders]="true" [c-maps-url]="cMapsUrl"></pdf-viewer>
      }
    </div>
    <div mat-dialog-actions class="ee-modal-actions">
      <button mat-button [mat-dialog-close]="null" type="button"
        data-tracking-event="pdf-preview-dialog-close">CLOSE</button>
      <button mat-flat-button color="accent" (click)="saveDocumentAsPdf()" type="button"
        data-tracking-event="pdf-preview-dialog-download">DOWNLOAD</button>
    </div>
  `,
  styles: [`
    ee-pdf-preview-dialog {
      .pdf-wrapper {
        flex: 1;
        min-height: 70vh;
        max-height: 100%;
        overflow: auto;
        display: flex;
        flex-direction: column;

        .viewer {
          flex: 1;
          min-height: 70vh;
          height: 100%;
          width: 100%;
          display: block;

          // The !important overrides the app-wide '.ng2-pdf-viewer-container { overflow-x: hidden
          // !important }' in shared-styles/components/_utils.scss, which otherwise clips a page
          // wider than the dialog instead of letting it scroll.
          .ng2-pdf-viewer-container {
            overflow: auto !important;
            width: 100%;
            height: 100%;
          }

          // pdf.js sizes .page as content-box: it writes the content width/height inline and adds
          // a 9px transparent border for the page shadow. Tailwind preflight's global
          // 'box-sizing: border-box' makes that border eat 18px out of the box, so the canvas
          // (100%/100% of .page) renders ~1.4% smaller than the JS-sized .annotationLayer and
          // .textLayer siblings. The offset is zero at the top-left of a page and grows toward the
          // bottom-right. Restore what pdf.js assumes.
          .pdfViewer .page {
            box-sizing: content-box;
          }
        }
      }

    }
  `],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class PdfPreviewDialogComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  docxFileMimeType = DOCX_FILE_MIME_TYPES;
  title = 'Preview';
  attachment: Attachment;
  hideTitle = false;
  pdfSrc: string | Uint8Array;
  cMapsUrl = `${PDFJS_ASSETS_URL}cmaps/`;

  private patchedViewer: PdfViewerComponent | undefined;

  constructor(private documentService: DocumentService, public cdr: ChangeDetectorRef, @Inject(MAT_DIALOG_DATA) public data: {
    attachment: Attachment,
    downloadUrl: string | Uint8Array,
    hideTitle: boolean
  }) {
    this.attachment = data.attachment;
    this.pdfSrc = data.downloadUrl;
    this.hideTitle = !!data.hideTitle;
    // PdfViewerComponent reads this in its own constructor — which runs after ours, when the
    // template creates <pdf-viewer> — to set pdfjs' GlobalWorkerOptions.workerSrc.
    (window as unknown as { pdfWorkerSrc: string }).pdfWorkerSrc = `${PDFJS_ASSETS_URL}pdf.worker.min.mjs`;
  }

  /**
   * ng2-pdf-viewer constructs its PDFViewer inside a `setTimeout` scheduled from the child's
   * `ngAfterViewChecked`, so this setter — which runs during the same change-detection pass —
   * always lands before the viewer options are read.
   */
  @ViewChild(PdfViewerComponent)
  set pdfViewerComponent(viewer: PdfViewerComponent | undefined) {
    if (viewer && viewer !== this.patchedViewer) {
      this.patchedViewer = viewer;
      this.renderFormFieldsIntoCanvas(viewer);
    }
  }

  ngOnInit(): void {
    this.title = this.attachment.original_filename ?? this.attachment.description ?? this.title;
  }

  saveDocumentAsPdf() {
    const filename = this.documentService.generateFilenameFromPath(this.attachment, PDF_FILE_EXTENSION);
    if (typeof this.pdfSrc === 'string') {
      this.documentService.saveDocumentFromUrl(filename, this.pdfSrc);
    } else {
      const blob = new Blob([this.pdfSrc.buffer as ArrayBuffer], { type: 'application/pdf' });
      this.documentService.saveDocument(filename, blob);
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  /**
   * pdf.js defaults to `AnnotationMode.ENABLE_FORMS`, which draws AcroForm fields as HTML inputs
   * layered over the page canvas. In a read-only preview that only causes problems: the overlay is
   * sized independently of the raster so it drifts out of alignment, the field text is re-typeset
   * by the browser rather than taken from the PDF's own appearance stream, and the inputs are
   * editable even though nothing persists what is typed into them. `AnnotationMode.ENABLE` makes
   * pdf.js paint the fields' appearance streams into the canvas instead, so the preview matches
   * the downloaded file exactly. Link annotations are still rendered as HTML.
   *
   * ng2-pdf-viewer 10.4.0 — the latest release, and it pins pdfjs-dist 4.8.69 as a hard dependency
   * — exposes no input for this, so we wrap its options factory. That method is private to the
   * library, hence the cast; if a future version renames it we silently keep stock behaviour.
   */
  private renderFormFieldsIntoCanvas(viewer: PdfViewerComponent): void {
    const internals = viewer as unknown as { getPDFOptions?: () => Record<string, unknown> };
    const getPdfOptions = internals.getPDFOptions;
    if (typeof getPdfOptions !== 'function') {
      return;
    }
    internals.getPDFOptions = () => ({
      ...getPdfOptions.call(viewer),
      annotationMode: AnnotationMode.ENABLE,
      imageResourcesPath: `${PDFJS_ASSETS_URL}images/`
    });
  }
}
